import React, { useContext, useState } from 'react';
import { Button } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import _ from 'lodash';
import ConfirmDialog from '@shared/components/confirmDialog/ConfirmDialog';
import MuiInputTextWithRemainingChars from '@shared/components/MuiInputTextWithRemainingChars';
import DeclineChangeDateRequestInputValidator from '../../../../validators/DeclineChangeDateRequestInputValidator';
import { CHANGE_DATE_REQUEST_STATUS } from '../../../../constants/ChangeDateRequestConstants';
import FormProviderWrapper from '@shared/components/confirmDialog/dialogWrappers/FormProviderWrapper';
import { DIALOG_MODES } from '@shared/components/confirmDialog/dialogWrappers/constants/DialogModes';
import LabeledSwitch from '@shared/components/LabeledSwitch';
import { useShowUnavailableAtmsStore } from './AdminEditEventStore';
import { useShallow } from 'zustand/shallow';
import { EventSettingsContext } from '../../../shared/contexts/EventSettingsContext';

const DECLINE_COMMENT_MIN_LENGTH = 25;
const DECLINE_COMMENT_MAX_LENGTH = 5000;
const DECLINE_COMMENT_INPUT_PLACEHOLDER = 'Describe requested changes for the appraisal details.';

/**
 * @typedef {object} ReviewChangeDateRequestProps
 * @property {Object} [changeDateRequestData]
 * @property {(status: number, declineComment?: string) => void | Promise<void>} handleChangeDateRequestReviewed
 */

/**
 * @param {ReviewChangeDateRequestProps} props
 */
const ReviewChangeDateRequest = props => {
  const { changeDateRequestData, handleChangeDateRequestReviewed } = props;

  const [showUnavailableAtms, toggleShowUnavailableAtms] = useShowUnavailableAtmsStore(
    useShallow(state => [state.showUnavailableAtms, state.toggleShowUnavailableAtms])
  );

  const settings = useContext(EventSettingsContext);

  const form = useForm({
    resolver: joiResolver(DeclineChangeDateRequestInputValidator.schema),
    defaultValues: {
      declineComment: ''
    }
  });

  const {
    formState: { errors },
    handleSubmit,
    control,
    reset
  } = form;

  const [openDeclineModal, setOpenDeclineModal] = useState(false);

  const handleConfirmDecline = async formData => {
    await handleChangeDateRequestReviewed(
      CHANGE_DATE_REQUEST_STATUS.DECLINED,
      formData.declineComment
    );
    setOpenDeclineModal(false);
  };

  const handleOnDeclineButtonClick = () => {
    setOpenDeclineModal(true);
  };

  const handleCloseDeclineModal = () => {
    setOpenDeclineModal(false);
    reset();
  };

  const handleOnApproveButtonClick = async () => {
    await handleChangeDateRequestReviewed(CHANGE_DATE_REQUEST_STATUS.APPROVED);
  };

  const declineDialogContent = (
    <div className='date-review-decline-dialog-content'>
      <div className='input-area'>
        <div className='label bold'>Comment*</div>
        <Controller
          control={control}
          name='declineComment'
          render={({ field }) => (
            <MuiInputTextWithRemainingChars
              name='declineComment'
              className='value'
              error={hasError(errors, 'declineComment')}
              disableUnderline
              multiline={true}
              rows={5}
              value={field.value}
              onChange={field.onChange}
              minLength={DECLINE_COMMENT_MIN_LENGTH}
              maxLength={DECLINE_COMMENT_MAX_LENGTH}
              hideRemainingChars={false}
              placeholder={DECLINE_COMMENT_INPUT_PLACEHOLDER}
            />
          )}
        />
      </div>
      <div></div>
    </div>
  );

  return (
    <div className='review-change-date-request'>
      <section className='page-section'>
        <div className='info-group'>
          <div className='section-title bold'>
            <span>Proposed Date</span>
          </div>
          <div>
            {changeDateRequestData.StartDate} to {changeDateRequestData.EndDate}
          </div>
        </div>
        <div className='review-action-group'>
          <div className='action-group'>
            <div className='action-button-group'>
              <Button
                className='approve-button secondary-button sm'
                onClick={handleOnApproveButtonClick}>
                Approve
              </Button>
              <Button
                data-testid='decline-change-request-dialog-button'
                className='delete-button-secondary secondary-button sm'
                onClick={handleOnDeclineButtonClick}>
                Decline
              </Button>
            </div>
          </div>
        </div>
      </section>
      <section className='page-section'>
        <div className='info-group'>
          <div className='section-title bold'>
            <span>Rationale</span>
          </div>
          <div className='section-description rationale'>{changeDateRequestData.SubmitComment}</div>
        </div>
        {settings.showEventAssignment && (
          <div className='action-group'>
            <LabeledSwitch
              testId='show-assignment-switcher'
              title='Preview'
              checkedLabel='On'
              uncheckedLabel='Off'
              onChange={toggleShowUnavailableAtms}
              checked={showUnavailableAtms}
            />
          </div>
        )}
      </section>
      <FormProviderWrapper form={form}>
        <ConfirmDialog
          dialogClassName='date-request-dialog'
          open={openDeclineModal}
          handleClose={handleCloseDeclineModal}
          dialogContent={declineDialogContent}
          handleOnConfirm={handleSubmit(handleConfirmDecline)}
          confirmBtnText='Decline'
          cancelBtnText='Cancel'
          errorMessage={errors?.declineComment?.message || ''}
          dialogMode={DIALOG_MODES.INTERNAL_FORM}
        />
      </FormProviderWrapper>
    </div>
  );
};

const hasError = (errors, fieldName) => Boolean(_.get(errors, fieldName));

export default ReviewChangeDateRequest;
